"use client";

import Image from "next/image";
import {
  Command,
  CommandList,
  CommandItem,
  CommandEmpty,
  CommandGroup,
  CommandInput,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Check, ChevronsUpDown } from "lucide-react";

// Define multi-chain token configuration for bridging
export interface BridgeTokenConfig {
  id: string;
  symbol: string;
  name: string;
  icon: string;
  decimals: number;
  bridgeProvider?: "layerzero" | "paxos"; // Optional bridge provider flag
  // Chain-specific configurations:
  ethereum: {
    contractAddress?: string; // undefined for native tokens
    isNative: boolean;
    symbol: string; // Network-specific symbol
    name: string; // Network-specific name
  };
  camp: {
    contractAddress?: string; // undefined for native tokens
    isNative: boolean;
    symbol: string; // Network-specific symbol
    name: string; // Network-specific name
  };
}

// Bridge-specific token configurations
export const BRIDGE_TOKENS: Record<string, BridgeTokenConfig> = {
  camp: {
    id: "camp",
    symbol: "CAMP",
    name: "Camp Token",
    icon: "/assets/camp-token.png",
    decimals: 18,
    ethereum: {
      isNative: false,
      contractAddress: "0x84EAac1B2dc3f84D92Ff84c3ec205B1FA74671fC", // CAMP OFT on Ethereum (bridged)
      symbol: "CAMP",
      name: "Camp Token",
    },
    camp: {
      isNative: true, // Native token on Camp Network
      contractAddress: undefined,
      symbol: "CAMP",
      name: "Camp Token",
    },
  },
  eth: {
    id: "eth",
    symbol: "ETH",
    name: "Ether",
    icon: "/ethereum-eth.svg",
    decimals: 18,
    ethereum: {
      isNative: true, // Native ETH on Ethereum
      contractAddress: undefined,
      symbol: "ETH",
      name: "Ether",
    },
    camp: {
      isNative: false,
      contractAddress: "0xb55066f2793773B3784f8c57c415a8b5932B33Cd",
      symbol: "ETH",
      name: "Ether",
    },
  },
  // USDC bridging via LayerZero OFT
  usdc: {
    id: "usdc",
    symbol: "USDC",
    name: "USDC",
    icon: "/usdc.svg",
    decimals: 6,
    ethereum: {
      isNative: false,
      contractAddress: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48", // USDC on Ethereum
      symbol: "USDC",
      name: "USDC",
    },
    camp: {
      isNative: false,
      contractAddress: "0x977fdEF62CE095Ae8750Fd3496730F24F60dea7a", // USDC OFT on Camp Network
      symbol: "USDC",
      name: "USDC",
    },
  },
};

// Get network-specific token symbol
export const getNetworkTokenSymbol = (
  token: BridgeTokenConfig,
  direction: "eth-to-camp" | "camp-to-eth",
): string => {
  if (direction === "eth-to-camp") {
    return token.ethereum.symbol;
  } else {
    return token.camp.symbol;
  }
};

// Get network-specific token name
export const getNetworkTokenName = (
  token: BridgeTokenConfig,
  direction: "eth-to-camp" | "camp-to-eth",
): string => {
  if (direction === "eth-to-camp") {
    return token.ethereum.name;
  } else {
    return token.camp.name;
  }
};

// Get available tokens for a specific network and direction
export const getAvailableTokens = (
  direction: "eth-to-camp" | "camp-to-eth",
): BridgeTokenConfig[] => {
  // All tokens are available on both networks, but order them based on network context
  if (direction === "eth-to-camp") {
    // From Ethereum: prioritize native ETH, then USDC, then CAMP
    return [BRIDGE_TOKENS.camp];
  } else {
    // From Camp: prioritize native CAMP, then USDC, then ETH
    return [BRIDGE_TOKENS.camp, BRIDGE_TOKENS.usdc, BRIDGE_TOKENS.eth];
  }
};

export const getBridgeToken = (
  tokenId: string,
): BridgeTokenConfig | undefined => {
  return BRIDGE_TOKENS[tokenId];
};

export const getDefaultBridgeToken = (
  direction: "eth-to-camp" | "camp-to-eth",
): BridgeTokenConfig => {
  // Get the first token from the prioritized list for each direction
  const availableTokens = getAvailableTokens(direction);
  return availableTokens[0]; // Returns ETH for eth-to-camp, CAMP for camp-to-eth
};

// Get the corresponding token representation for the opposite network
export const getCorrespondingToken = (
  token: BridgeTokenConfig,
  targetDirection: "eth-to-camp" | "camp-to-eth",
): { symbol: string; name: string } => {
  if (targetDirection === "eth-to-camp") {
    return {
      symbol: token.camp.symbol,
      name: token.camp.name,
    };
  } else {
    return {
      symbol: token.ethereum.symbol,
      name: token.ethereum.name,
    };
  }
};

// Check if a token is native on a specific network
export const isNativeOnNetwork = (
  token: BridgeTokenConfig,
  direction: "eth-to-camp" | "camp-to-eth",
): boolean => {
  if (direction === "eth-to-camp") {
    return token.ethereum.isNative;
  } else {
    return token.camp.isNative;
  }
};

interface BridgeTokenSelectorProps {
  selectedToken: BridgeTokenConfig | null;
  onTokenSelect: (token: BridgeTokenConfig | null) => void;
  label: string;
  direction: "eth-to-camp" | "camp-to-eth";
}

export function BridgeTokenSelector({
  selectedToken,
  onTokenSelect,
  label,
  direction,
}: BridgeTokenSelectorProps) {
  const [open, setOpen] = useState(false);

  // Get tokens available for the current direction
  const availableTokens = getAvailableTokens(direction);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          role="combobox"
          variant="outline"
          aria-expanded={open}
          className="w-52 cursor-pointer justify-between border-none bg-[#FFE1CB] p-6 hover:bg-[#FFE1CB]/70 max-sm:w-full max-sm:p-4">
          {selectedToken ? (
            <div className="flex items-center gap-2">
              <Image
                priority
                width={24}
                height={24}
                alt={getNetworkTokenName(selectedToken, direction)}
                src={selectedToken.icon}
                className="p-0.1 rounded-full bg-[#FDFDFC] object-contain max-sm:h-6 max-sm:w-6"
              />
              <div className="flex flex-col items-start">
                <span className="text-sm font-medium max-sm:text-base">
                  {getNetworkTokenSymbol(selectedToken, direction)}
                </span>
              </div>
            </div>
          ) : (
            <p className="font-sans-serif ml-2 text-[#1A1A1A] max-sm:text-sm">
              Select {label} token...
            </p>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-60 bg-[#FDFDFC] p-0 max-sm:w-[calc(100vw-2rem)]">
        <Command>
          <CommandInput placeholder="Search token..." className="h-9" />
          <CommandList>
            <CommandEmpty>No token found.</CommandEmpty>
            <CommandGroup className="bg-[#FDFDFC]">
              {availableTokens.map((token) => (
                <CommandItem
                  key={token.id}
                  value={token.symbol}
                  onSelect={() => {
                    const newToken =
                      selectedToken?.id === token.id ? null : token;
                    onTokenSelect(newToken);
                    setOpen(false);
                  }}>
                  <Image
                    priority
                    width={24}
                    height={24}
                    alt={getNetworkTokenName(token, direction)}
                    src={token.icon}
                    className="mr-2 rounded-sm object-contain"
                  />
                  <div className="flex flex-1 flex-col max-sm:gap-1">
                    <div className="flex items-center gap-2">
                      <span className="font-sans text-sm font-medium text-[#1A1A1A] max-sm:text-base">
                        {getNetworkTokenName(token, direction)}
                      </span>
                      {isNativeOnNetwork(token, direction) && (
                        <span className="rounded-full bg-green-100 px-1.5 py-0.5 text-xs font-medium text-green-700">
                          Native
                        </span>
                      )}
                    </div>
                    <span className="font-sans text-xs font-normal text-[#1A1A1A]/80 max-sm:text-sm">
                      {getNetworkTokenSymbol(token, direction)}
                    </span>
                  </div>
                  <Check
                    className={cn(
                      "ml-2 h-4 w-4",
                      selectedToken?.id === token.id
                        ? "opacity-100"
                        : "opacity-0",
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
